import axios from 'axios'
import { ElMessage } from 'element-plus'

// 使用环境变量配置的API地址，开发环境为http://localhost:3000
const service = axios.create({
  baseURL: '/api',  // 使用相对路径
  timeout: 120000  // 增加到120秒，适应大数据量查询
})

// 403 toast 去重：同一短窗口内并发的多个 403 只弹一次，
// 避免一个页面里多个接口同时 403 时弹多条「没有权限访问」。
let forbiddenToastLocked = false
function showForbiddenToastOnce() {
  if (forbiddenToastLocked) return
  forbiddenToastLocked = true
  ElMessage.error('没有权限访问')
  setTimeout(() => {
    forbiddenToastLocked = false
  }, 1500)
}

// 请求拦截器
service.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // 复习题 ZIP 导出可能耗时较长（按学员逐个生成），避免默认 120s 超时
    try {
      const url = String(config.url || '')
      const isBctReviewZipExportApi = /\/class-sessions\/[^/]+\/reports\/export-bct-review-zip\b/i.test(url)
      if (isBctReviewZipExportApi) {
        const DEFAULT_EXPORT_TIMEOUT = 600000 // 10min
        if (typeof config.timeout !== 'number' || Number.isNaN(config.timeout)) {
          config.timeout = DEFAULT_EXPORT_TIMEOUT
        } else {
          config.timeout = Math.max(config.timeout, DEFAULT_EXPORT_TIMEOUT)
        }
      }
    } catch (e) {
      void e
    }

    return config
  },
  error => {
    // 不要打印 error 本身：其 config.data 可能含登录明文口令。
    console.error('请求错误:', error?.message || error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    // 如果是下载文件请求，直接返回响应数据
    if (response.config.responseType === 'blob') {
      return response.data
    }

    const res = response.data
    
    // 检查响应状态
    if (res.status === 'success' || res.status === 'conflict' || res.status === 'preflight_required' || res.success === true) {
      return res
    }
    
    // 如果响应不成功，但有具体的错误信息
    if (res.message) {
      ElMessage.error(res.message)
      return Promise.reject(new Error(res.message))
    }
    
    // 默认错误处理
    ElMessage.error('请求失败')
    return Promise.reject(new Error('请求失败'))
  },
  async error => {
    // 不要打印 error 本身：其 config.data 可能含登录明文口令。
    console.error('响应错误:', {
      url: error?.config?.url,
      method: error?.config?.method,
      status: error?.response?.status,
      code: error?.code,
      message: error?.message
    })

    // 当 responseType=blob 时，后端的 JSON 错误会被包成 Blob，先解包拿真实 message
    const isBlobResponse = error?.config?.responseType === 'blob'
    const extractBlobErrorMessage = async () => {
      try {
        const data = error?.response?.data
        if (!(data instanceof Blob)) return ''
        const text = await data.text()
        if (!text) return ''
        try {
          const parsed = JSON.parse(text)
          return parsed?.message || parsed?.error || text
        } catch (e) {
          void e
          return text
        }
      } catch (e) {
        void e
        return ''
      }
    }

    const blobErrorMessage = isBlobResponse ? await extractBlobErrorMessage() : ''

    // 处理401错误（登录接口本身的401不跳转，让页面自行展示错误）
    if (error.response?.status === 401) {
      if (!error.config?.url?.includes('/users/login')) {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        localStorage.removeItem('role')
        localStorage.removeItem('isAdmin')
        localStorage.removeItem('tokenExpiresAt')
        window.location.href = '/auth/login'
      }
      return Promise.reject(error)
    }

    if (error.code === 'ECONNABORTED') {
      const failedUrl = String(error?.config?.url || '')
      const isBctReviewZipExportApi = /\/class-sessions\/[^/]+\/reports\/export-bct-review-zip\b/i.test(failedUrl)
      ElMessage.error(
        isBctReviewZipExportApi
          ? '导出超时：学员较多或题目较多，请稍后重试'
          : '请求超时，请稍后重试'
      )
      return Promise.reject(error)
    }

    if (error.response) {
      switch (error.response.status) {
        case 403:
          showForbiddenToastOnce()
          break
        case 404:
          ElMessage.error(error.response.data?.message || '请求的资源不存在')
          break
        default:
          ElMessage.error(blobErrorMessage || error.response.data?.message || '请求失败')
      }
    } else if (error.request) {
      ElMessage.error('网络错误，请检查网络连接')
    } else {
      ElMessage.error('请求错误')
    }
    return Promise.reject(error)
  }
)

export default service
